<script>
import AkTitleBackgroundText from '@components/v2/general/AkTitleBackgroundText.vue';
import utilsMixin from '@mixins/utilsMixin';
import AkLabel from '@components/v2/general/AkLabel.vue';
import revenuemanagementService from '@services/revenuemanagementService';
import revenueManagementMixin from '@mixins/revenueManagementMixin';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import AkShimmerGroup from '@components/v2/general/AkShimmerGroup.vue';
import RatingStar from '@views/revenuemanagement/RatingStar.vue';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';
import ownerService from '@services/ownerService';
import AkDialog from '@components/general/AkDialog.vue';
import AkInputMoney from '@components/v2/input/AkInputMoney.vue';
import useVuelidate from '@vuelidate/core';
import {maxValue, required} from '@vuelidate/validators';
import messageMixin from '@mixins/messageMixin';

export default {
  name: 'globablStats',
  components: {AkInputMoney, AkDialog, RatingStar, AkShimmerGroup, AkShimmer, AkLabel, AkTitleBackgroundText},
  mixins: [utilsMixin, revenueManagementMixin, permissionsMixinComputed, messageMixin],
  data() {
    return {
      globalStats: {
        nbNightsRentedPrevYear: null,
        nbNightsRented: null,
        annualObjective: null,
        annualRevenue: null,
        clientOccupationRatePrevYear: null,
        clientOccupationRate: null,
        globalRatingPrevYear: null,
        globalRating: null,
      },
      loading: true,
      scrollIndex: 0,
      revenueObjective: null,
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      revenueObjective: {required, maxValue: maxValue(100000000)},
    };
  },
  methods: {
    handleScroll(event) {
      if (this.$refs.carousel) {
        const scrollPosition = event.target.scrollLeft;
        const width = event.target.clientWidth;
        this.scrollIndex = Math.round(scrollPosition / width);
      }
    },
    fetchGlobalStats() {
      this.loading = true;
      revenuemanagementService.getGlobalStats().then((data) => {
        this.globalStats = data;
        this.revenueObjective = {...data}.annualObjective;
        this.loading = false;
      });
    },
    showObjectiveDialog() {
      this.$refs.dialog.show();
    },
    setObjective() {
      this.v$.$touch();
      if (this.v$.$error) {
        console.log(this.v$);
        this.$refs.dialog.init();
        return;
      }
      ownerService.updateRevenueObjective(this.revenueObjective).then(() => {
        this.globalStats.annualObjective = JSON.parse(JSON.stringify(this.revenueObjective));
        this.$refs.dialog.cancel();
        this.success('Votre objectif annuel a été modifié avec succès');
      });
    },
  },
  mounted() {
    this.fetchGlobalStats();
  },
  computed: {
    annualRevenueProgression() {
      if (this.loading) return 0;
      if (!this.globalStats.annualObjective) return 100;
      const progression = Math.floor((this.globalStats.annualRevenue / this.globalStats.annualObjective) * 100);
      return Math.min(100, Math.max(0, progression));
    },
    diffNightsRentedPrevYear() {
      if (!this.globalStats.nbNightsRentedPrevYear) return this.globalStats.nbNightsRented.toFixed(1);
      return (
        ((this.globalStats.nbNightsRented - this.globalStats.nbNightsRentedPrevYear) /
          this.globalStats.nbNightsRentedPrevYear) *
        100
      ).toFixed(1);
    },
    diffClientOccupationRatePrevYear() {
      if (!this.globalStats.clientOccupationRatePrevYear) return this.globalStats.clientOccupationRate.toFixed(1);
      return (
        ((this.globalStats.clientOccupationRate - this.globalStats.clientOccupationRatePrevYear) /
          this.globalStats.clientOccupationRatePrevYear) *
        100
      ).toFixed(1);
    },
    diffGlobalRatingPrevYear() {
      if (!this.globalStats.globalRatingPrevYear) return this.globalStats.globalRating.toFixed(1);
      return (
        ((this.globalStats.globalRating - this.globalStats.globalRatingPrevYear) /
          this.globalStats.globalRatingPrevYear) *
        100
      ).toFixed(1);
    },
  },
};
</script>

<template>
  <AkShimmerGroup :loading="loading">
    <div class="bg-secondary-50 -mx-5">
      <div class="max-w-[1400px] mx-auto md:p-12 p-4 flex flex-col md:gap-6 gap-4 -mt-5">
        <!--TODO : virer le margin négatif-->
        <div class="flex gap-2 items-center text-grey-900">
          <i class="ga-icon ga-stats text-[32px]" />
          <AkTitleBackgroundText text="Mes __statistiques__" class-name="text-[32px] font-bold" />
        </div>

        <div
          class="rounded-xl bg-white p-6 shadow-[-4px_-2px_12px_0_rgba(63,_180,_229,_0.05),_0_1px_20px_0_rgba(75,_149,_255,_0.08);] flex gap-8">
          <div class="h-[69px] min-w-[69px] bg-[#E9F0FF] rounded-full flex justify-center">
            <i class="ga-icon ga-lune text-[32px] place-self-center" />
          </div>
          <div class="flex flex-col text-grey-900">
            <div class="text-md font-semibold flex items-center gap-2">
              <AkShimmer width="61px" height="48px">
                <span class="text-[32px] font-bold leading-[48px]">{{ formatNumber(globalStats.nbNightsRented) }}</span>
              </AkShimmer>
              nuits louées
            </div>
            <span class="flex flex-wrap items-center gap-2 text-sm text-grey-500">
              Année dernière
              <AkShimmer width="120px" height="18px">
                <span class="text-grey-900">{{ formatNumber(globalStats.nbNightsRentedPrevYear) }} nuits louées</span>
                <template v-if="diffNightsRentedPrevYear != 0">
                  <i v-if="diffNightsRentedPrevYear > 0" class="ga-icon ga-trend_up text-md text-green"></i>
                  <i v-else class="ga-icon ga-trend_down text-md text-red"></i>
                  <span class="font-semibold" :class="diffNightsRentedPrevYear > 0 ? 'text-green' : 'text-red'">
                    {{ formatNumber(diffNightsRentedPrevYear) }} %
                  </span>
                </template>
              </AkShimmer>
            </span>
          </div>
        </div>

        <div class="relative">
          <div
            ref="carousel"
            @scroll="handleScroll"
            class="flex flex-row md:flex-wrap lg:flex-nowrap overflow-x-auto snap-x snap-mandatory md:overflow-visible no-scrollbar gap-4 pb-8 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
            <div
              class="snap-center shrink-0 md:shrink w-full flex flex-col shadow-[0_1px_20px_0_rgba(75,_149,_255,_0.08);] p-6 gap-4 bg-[linear-gradient(67deg,_rgba(0,_45,_109,_0.00)_24.75%,_#002D6D_111.79%)] bg-[#172344] rounded-xl lg:w-1/2">
              <div class="flex gap-6 items-center">
                <div class="bg-white rounded-lg flex justify-center items-center h-[68px] min-w-[68px]">
                  <i class="ga-icon text-primary ga-coins text-[32px]" />
                </div>
                <span class="text-white text-[18px] font-semibold">Volume d'affaires annuel</span>
              </div>
              <div class="flex flex-col gap-2">
                <AkShimmer height="48px" width="95px" color="#1e2d57">
                  <span class="text-white text-[32px] font-bold">{{ formatNumber(globalStats.annualRevenue) }} €</span>
                </AkShimmer>
                <span class="text-grey-100 flex gap-2 items-center">
                  Sur
                  <AkShimmer width="23px" color="#1e2d57">
                    <span class="text-white font-semibold">{{ formatNumber(globalStats.annualObjective) }} €</span>
                  </AkShimmer>
                  <i
                    v-if="isOwner"
                    class="ga-icon ga-pencil text-grey-100 text-[18px] cursor-pointer hover:text-white"
                    @click="showObjectiveDialog" />
                </span>
                <div class="w-full rounded-full bg-secondary-50">
                  <AkShimmer width="100%" height="8px" color="#1e2d57">
                    <div
                      class="bg-green rounded-full h-2 loadBar"
                      :style="`--target-width:${annualRevenueProgression}%`"></div>
                  </AkShimmer>
                </div>
                <span class="place-self-end text-green font-semibold">
                  <AkShimmer width="40px" color="#1e2d57" height="21px">
                    {{ formatNumber(annualRevenueProgression) }} %
                  </AkShimmer>
                </span>
              </div>
            </div>

            <div
              class="snap-center shrink-0 md:shrink w-full bg-white rounded-xl p-6 justify-between flex flex-col lg:w-1/4 text-grey-900">
              <div class="flex items-center gap-2">
                <div class="bg-green-50 rounded-lg flex justify-center items-center h-[68px] min-w-[68px]">
                  <i class="ga-icon text-green ga-3users text-[32px]" />
                </div>
                <AkLabel
                  class="font-semibold !mb-0"
                  information="Part des nuits réellement louées par des voyageurs, sur les nuits ouvertes à la réservation. C’est un indicateur clé de votre performance commerciale.">
                  Taux d'occupation voyageur
                </AkLabel>
              </div>
              <AkShimmer width="90px" height="48px">
                <span class="flex items-center gap-1.5 text-[32px] font-bold">
                  {{ formatNumber(globalStats.clientOccupationRate) }}
                  <span class="text-grey-500 text-md">%</span>
                </span>
              </AkShimmer>
              <span class="flex items-center gap-2 text-sm text-grey-500">
                Année dernière
                <AkShimmer width="120px" height="18px">
                  <span class="text-grey-900">{{ formatNumber(globalStats.clientOccupationRatePrevYear) }} %</span>
                  <template v-if="diffClientOccupationRatePrevYear != 0">
                    <i v-if="diffClientOccupationRatePrevYear > 0" class="ga-icon ga-trend_up text-md text-green"></i>
                    <i v-else class="ga-icon ga-trend_down text-md text-red"></i>
                    <span
                      class="font-semibold"
                      :class="diffClientOccupationRatePrevYear > 0 ? 'text-green' : 'text-red'">
                      {{ formatNumber(diffClientOccupationRatePrevYear) }} %
                    </span>
                  </template>
                </AkShimmer>
              </span>
            </div>

            <div
              class="snap-center shrink-0 md:shrink w-full bg-white rounded-xl p-6 justify-between flex flex-col lg:w-1/4 text-grey-900">
              <div class="flex items-center gap-2">
                <div class="bg-secondary-50 rounded-lg flex justify-center items-center h-[68px] min-w-[68px]">
                  <i class="ga-icon text-secondary ga-star_sharp text-[32px]" />
                </div>
                <AkLabel class="font-semibold !mb-0">Note globale</AkLabel>
              </div>
              <AkShimmer width="150px" height="48px">
                <span class="flex items-center gap-2 text-[32px] font-bold">
                  {{ formatNumber(globalStats.globalRating) }}
                  <span class="text-grey-500 text-md">/10</span>

                  <RatingStar :indicator="globalStats.globalRating * 10" />
                </span>
              </AkShimmer>
              <span class="flex items-center gap-2 text-sm text-grey-500">
                Année dernière
                <AkShimmer width="120px" height="18px">
                  <span class="text-grey-900">{{ formatNumber(globalStats.globalRatingPrevYear) }}/10</span>
                  <template v-if="diffGlobalRatingPrevYear != 0">
                    <i v-if="diffGlobalRatingPrevYear > 0" class="ga-icon ga-trend_up text-md text-green"></i>
                    <i v-else class="ga-icon ga-trend_down text-md text-red"></i>
                    <span class="font-semibold" :class="diffGlobalRatingPrevYear > 0 ? 'text-green' : 'text-red'">
                      {{ formatNumber(diffGlobalRatingPrevYear) }} %
                    </span>
                  </template>
                </AkShimmer>
              </span>
            </div>
          </div>
          <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
            <div
              v-for="i in 3"
              :key="i"
              class="h-2 w-2 rounded-full transition-colors duration-300"
              :class="scrollIndex === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
          </div>
        </div>
      </div>
    </div>
  </AkShimmerGroup>
  <AkDialog title="Modifier l'objectif annuel" ref="dialog" @validate="setObjective" validate-label="Modifier">
    <AkInputMoney
      v-model="this.revenueObjective"
      :label="$t('accommodation.annualProfitTarget')"
      :information="$t('informations.annualProfitTarget')"
      :validator="v$.revenueObjective" />
  </AkDialog>
</template>

<style scoped>
.loadBar {
  --target-width: 0px;
  animation: load forwards ease-out 1500ms;
}

@keyframes load {
  from {
    width: 0%;
  }
  to {
    width: var(--target-width);
  }
}
</style>
